import type { RoutingSkuTargetRecord } from '@nadoutong/contracts';

/**
 * How a SKU's pool splits first-attempt traffic across its targets (#1495).
 *
 * The page's whole purpose is "this SKU fans out to N upstreams in these
 * proportions", but that was only ever readable as a raw `weight` column, so
 * an operator had to compute `80 / (80 + 40 + 20)` by hand to learn what
 * share an upstream actually carries.
 *
 * The semantics are the ones `tenant_routing.pool.weight_hint` already states,
 * and this must not be presented as anything stronger: weight decides how NEW
 * SESSIONS are distributed across targets, because a session is pinned to one
 * target to keep its prompt cache. It is not a per-request percentage split.
 */

/**
 * Why a target does or does not carry first-attempt traffic.
 *
 *   - `active`   — enabled with a positive weight; carries `percent` of it.
 *   - `reserve`  — enabled with weight 0. The documented "纯兜底" case: it is a
 *                  retry/fallback candidate only and takes no first-attempt
 *                  traffic, so it is excluded from the denominator rather than
 *                  shown as 0%.
 *   - `disabled` — switched off, so it takes no traffic at all. Also excluded:
 *                  counting a disabled target's weight would understate every
 *                  other target's real share.
 */
export type TargetShareKind = 'active' | 'reserve' | 'disabled';

export interface TargetShare {
  readonly targetId: string;
  readonly kind: TargetShareKind;
  /**
   * Whole-percent share of first-attempt placements. Always 0 unless `kind` is
   * `active`, and across one pool the `active` values sum to exactly 100 (or
   * to 0 when the pool has no active target at all).
   */
  readonly percent: number;
}

function shareKind(target: RoutingSkuTargetRecord): TargetShareKind {
  if (!target.enabled) return 'disabled';
  return target.weight > 0 ? 'active' : 'reserve';
}

/**
 * Per-target first-attempt share, in the order the targets were given.
 *
 * Percentages are whole numbers that sum to exactly 100, via the
 * largest-remainder method: floor everything, then hand the leftover points to
 * the largest fractional remainders (ties broken by position, so the result is
 * deterministic). Rounding each share independently is what produces the
 * 33/33/33 = 99% and 17/17/17/17/17/17 = 102% displays this is meant to avoid
 * — an operator reading a total that is not 100 cannot tell whether it is a
 * rounding artifact or a real gap in the config.
 */
export function firstAttemptShares(
  targets: readonly RoutingSkuTargetRecord[],
): readonly TargetShare[] {
  const kinds = targets.map((target) => ({ target, kind: shareKind(target) }));
  const totalWeight = kinds.reduce(
    (sum, entry) => (entry.kind === 'active' ? sum + entry.target.weight : sum),
    0,
  );

  // No target is both enabled and weighted: an all-reserve, all-disabled or
  // empty pool. There is no denominator, so nothing gets a percentage — as
  // opposed to everything getting 0%, which would read as a live split.
  if (totalWeight <= 0) {
    return kinds.map((entry) => ({ targetId: entry.target.id, kind: entry.kind, percent: 0 }));
  }

  const exact = kinds.map((entry) =>
    entry.kind === 'active' ? (entry.target.weight * 100) / totalWeight : 0,
  );
  const floors = exact.map((value) => Math.floor(value));
  const leftover = 100 - floors.reduce((sum, value) => sum + value, 0);

  // Each active target loses < 1 point to flooring, so `leftover` never exceeds
  // the number of active targets and one extra point each is always enough.
  const bonusIndexes = new Set(
    kinds
      .map((entry, index) => ({
        index,
        kind: entry.kind,
        remainder: (exact[index] ?? 0) - (floors[index] ?? 0),
      }))
      .filter((entry) => entry.kind === 'active')
      .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
      .slice(0, Math.max(0, leftover))
      .map((entry) => entry.index),
  );

  return kinds.map((entry, index) => ({
    targetId: entry.target.id,
    kind: entry.kind,
    percent: (floors[index] ?? 0) + (bonusIndexes.has(index) ? 1 : 0),
  }));
}

/** Lookup by target id, for rows that render their own share independently. */
export function shareByTargetId(
  shares: readonly TargetShare[],
): ReadonlyMap<string, TargetShare> {
  return new Map(shares.map((share) => [share.targetId, share]));
}
